// firebase.js - COMPLETE VERSION WITH ALL EXPORTS
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  deleteToken,
} from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_PRIJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_VAPID_KEY;

// Singleton instances
let app = null;
let messaging = null;

// ============ CORE FUNCTIONS ============

// Initialize Firebase once
export const initializeFirebase = async () => {
  if (app) return { app, messaging };

  app = initializeApp(firebaseConfig);

  const supported = await isSupported();
  if (supported) {
    messaging = getMessaging(app);
  }

  return { app, messaging };
};

// Get messaging instance
export const getMessagingInstance = async () => {
  if (!messaging) {
    await initializeFirebase();
  }
  return messaging;
};

// ============ TOKEN MANAGEMENT ============

// Get FCM token with cross-browser support
export const getFCMToken = async (requestPermission = true) => {
  try {
    console.log("🔄 Getting FCM Token...");

    await initializeFirebase();

    if (!messaging) {
      console.warn("⚠️ Firebase Messaging not supported");
      return null;
    }

    // Check/request permission
    let permission = Notification.permission;

    if (requestPermission && permission === "default") {
      console.log("🔔 Requesting notification permission...");
      permission = await Notification.requestPermission();
      console.log("📋 Permission result:", permission);
    }

    if (permission !== "granted") {
      console.warn("🔕 Notification permission not granted:", permission);
      return null;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/" }
    );

    // Check VAPID key
    if (!VAPID_KEY || VAPID_KEY.length < 50) {
      console.error("❌ Invalid or missing VAPID key");
      console.error(
        "   Get it from: Firebase Console → Project Settings → Cloud Messaging"
      );
      return null;
    }

    // Get token with VAPID key
    console.log("🔑 Requesting token with VAPID key...");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn("⚠️ No token received from Firebase");
      return null;
    }

    // Store token
    console.log("✅ Token received:", token.substring(0, 30) + "...");
    localStorage.setItem("fcmToken", token);
    localStorage.setItem("fcmTokenTimestamp", Date.now().toString());

    return token;
  } catch (error) {
    console.error("❌ Error getting FCM token:", error);

    if (error.code === "messaging/permission-blocked") {
      console.warn("🔒 Notifications blocked by user in browser settings");
    } else if (error.code === "messaging/invalid-vapid-key") {
      console.error(
        "🔑 Invalid VAPID key. Check Firebase Console configuration"
      );
    }

    return null;
  }
};

// Get current stored token
export const getCurrentToken = () => {
  return localStorage.getItem("fcmToken");
};

// Get stored token (alias)
export const getStoredFCMToken = () => {
  return getCurrentToken();
};

// Check if token needs refresh (older than 7 days)
export const shouldRefreshToken = () => {
  const timestamp = localStorage.getItem("fcmTokenTimestamp");
  if (!timestamp) return true;

  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - parseInt(timestamp) > sevenDays;
};

// Request notification permission (simple wrapper)
export const requestNotificationPermission = async () => {
  try {
    if (!("Notification" in window)) {
      console.warn("⚠️ Notifications not supported");
      return false;
    }

    // Only request if permission is default
    if (Notification.permission !== "default") {
      console.log("✅ Permission already:", Notification.permission);
      return Notification.permission === "granted";
    }

    const permission = await Notification.requestPermission();
    console.log("📋 Permission status:", permission);

    return permission === "granted";
  } catch (error) {
    console.error("❌ Error requesting permission:", error);
    return false;
  }
};

// ============ MESSAGE HANDLING ============

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  getMessagingInstance().then((messagingInstance) => {
    if (messagingInstance) {
      return onMessage(messagingInstance, (payload) => {
        console.log("📩 Foreground message received:", payload);
        if (callback) callback(payload);
      });
    }
    return () => {}; // Return empty cleanup function
  });

  // Return cleanup function
  return () => {
    console.log("🧹 Cleaning up message listener");
  };
};

// Alias for compatibility
export const onMessageListener = onForegroundMessage;

// ============ TOKEN MANAGEMENT (EXTENDED) ============

// Delete FCM token
export const deleteFCMToken = async () => {
  try {
    const messagingInstance = await getMessagingInstance();
    if (messagingInstance) {
      // Try to delete from Firebase
      const token = getCurrentToken();
      if (token) {
        try {
          await deleteToken(messagingInstance);
          console.log("✅ Token deleted from Firebase");
        } catch (error) {
          console.log("⚠️ Could not delete from Firebase (might be OK)");
        }
      }
    }

    // Clear local storage
    localStorage.removeItem("fcmToken");
    localStorage.removeItem("fcmTokenTimestamp");
    console.log("✅ Local storage cleared");

    return true;
  } catch (error) {
    console.error("❌ Error deleting token:", error);
    return false;
  }
};

// Send token to server
export const sendTokenToServer = async (token, userId = null) => {
  try {
    const userToken =
      localStorage.getItem("ecoToken") || sessionStorage.getItem("ecoToken");

    if (!userToken) {
      console.log("⚠️ User not logged in, token saved for later");
      localStorage.setItem("pendingFCMToken", token);
      return false;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/save-fcm-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          fcmToken: token,
          userId: userId,
          deviceId: `WEB_${navigator.userAgent.substring(0, 30)}`,
          platform: "web",
          timestamp: new Date().toISOString(),
        }),
      }
    );

    if (response.ok) {
      console.log("✅ Token sent to server successfully");
      localStorage.removeItem("pendingFCMToken");
      return true;
    }

    console.warn("⚠️ Server rejected token");
    return false;
  } catch (error) {
    console.error("❌ Error sending token to server:", error);
    return false;
  }
};

// ============ DEBUG & UTILITY FUNCTIONS ============

// Debug function
export const debugFCMToken = async () => {
  console.log("=== FCM Debug ===");
  console.log("1. Browser:", navigator.userAgent);
  console.log("2. HTTPS:", window.location.protocol === "https:");
  console.log("3. Permission:", Notification.permission);
  console.log("4. VAPID Key:", VAPID_KEY ? "✅ Set" : "❌ Missing");
  console.log("5. VAPID Key length:", VAPID_KEY?.length);

  const token = getCurrentToken();
  console.log("6. Current Token:", token ? "✅ Exists" : "❌ None");
  if (token) console.log("   Token:", token.substring(0, 30) + "...");

  const timestamp = localStorage.getItem("fcmTokenTimestamp");
  if (timestamp) {
    const age = Date.now() - parseInt(timestamp);
    console.log("7. Token age:", Math.floor(age / (1000 * 60 * 60)), "hours");
  }

  console.log("8. Needs refresh:", shouldRefreshToken());
  console.log("=== End Debug ===");
};

// Get token status
export const getTokenStatus = () => {
  const token = getCurrentToken();
  const timestamp = localStorage.getItem("fcmTokenTimestamp");

  return {
    hasToken: !!token,
    token: token,
    tokenPreview: token ? token.substring(0, 20) + "..." : null,
    permission: Notification.permission,
    needsRefresh: shouldRefreshToken(),
    timestamp: timestamp
      ? new Date(parseInt(timestamp)).toLocaleString()
      : "Never",
    age: timestamp ? Date.now() - parseInt(timestamp) : null,
  };
};

// Initialize FCM (alias for backward compatibility)
export const initializeFCM = async (requestPermission = true) => {
  return await getFCMToken(requestPermission);
};

// Check if messaging is supported
export const isMessagingSupported = async () => {
  return await isSupported();
};

// Check browser support
export const checkBrowserSupport = () => {
  const isHTTPS = window.location.protocol === "https:";
  const isLocalhost = window.location.hostname === "localhost";
  const hasServiceWorker = "serviceWorker" in navigator;
  const hasNotifications = "Notification" in window;
  const hasPushManager = "PushManager" in window;

  return {
    isHTTPS,
    isLocalhost,
    hasServiceWorker,
    hasNotifications,
    hasPushManager,
    isSecure: isHTTPS || isLocalhost,
    isSupported: hasServiceWorker && hasNotifications && hasPushManager,
  };
};

// Browser detection
export const detectBrowser = () => {
  const ua = navigator.userAgent;
  return {
    isChrome: /chrome|chromium|crios/i.test(ua) && !/edge|edg/i.test(ua),
    isFirefox: /firefox|fxios/i.test(ua),
    isSafari: /safari/i.test(ua) && !/chrome/i.test(ua),
    isEdge: /edge|edg/i.test(ua),
    isIOS: /iphone|ipad|ipod/i.test(ua),
    isAndroid: /android/i.test(ua),
    isMobile: /mobi/i.test(ua),
  };
};

// Default export
export default {
  initializeFirebase,
  getFCMToken,
  getCurrentToken,
  getStoredFCMToken,
  shouldRefreshToken,
  requestNotificationPermission,
  onForegroundMessage,
  onMessageListener,
  deleteFCMToken,
  sendTokenToServer,
  debugFCMToken,
  getTokenStatus,
  initializeFCM,
  isMessagingSupported,
  checkBrowserSupport,
  detectBrowser,
  getMessagingInstance,
};
